<template>
    <admin-page :result="result" description="Invitaciones pendientes de alumnos y de entrenadores">
        <div class="inv-toolbar">
            <segmented-tabs class="inv-tabs" :options="typeOptions" :value="type" @change="onChangeType" />
            <kimsa-text-field class="inv-search" @change="search = $event" :value="search" placeholder="Filtrar por email"
                prepend-inner-icon="mdi-magnify" dense hide-details />
            <button-tooltip tooltip="Actualizar">
                <template #activator="{ on, attrs }">
                    <v-btn icon small class="inv-refresh" :loading="loading" v-on="on" v-bind="attrs"
                        @click="loadInvitations">
                        <v-icon size="18">mdi-refresh</v-icon>
                    </v-btn>
                </template>
            </button-tooltip>
        </div>

        <skeleton-loader v-if="loading" type="rows" :count="5" class="mt-4" />

        <div v-else-if="filtered.length" class="inv-list">
            <div class="inv-select-bar">
                <button class="inv-check" @click="toggleAll">
                    <v-icon size="20" :color="allSelected ? 'accent' : 'txtMuted'">
                        {{ allSelected ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}
                    </v-icon>
                </button>
                <span v-if="selected.length" class="inv-selected-count">{{ selected.length }} seleccionada(s)</span>
                <span v-else class="inv-selected-count">Seleccionar todas las de esta página</span>
                <v-btn v-if="selected.length" text small color="error" class="inv-bulk"
                    @click="$refs.confirmBulk.show()">
                    Borrar seleccionadas
                </v-btn>
            </div>

            <div v-for="invitation in paged" :key="invitation.id" class="inv-row"
                :class="{ 'inv-row--selected': isSelected(invitation) }">
                <button class="inv-check" @click="toggleSelect(invitation)">
                    <v-icon size="20" :color="isSelected(invitation) ? 'accent' : 'txtMuted'">
                        {{ isSelected(invitation) ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}
                    </v-icon>
                </button>
                <div class="inv-body">
                    <div class="inv-email">{{ invitation.email }}</div>
                    <div class="inv-meta">
                        {{ invitation.fromLabel }} · {{ invitation.dateLabel }}
                    </div>
                </div>
                <span class="inv-status">{{ invitation.status }}</span>
                <button-tooltip text :tooltip="resendTooltip" :loading="resendingId === invitation.id"
                    @click="showResend(invitation)">
                    <v-icon size="18" color="secondary">mdi-email-sync-outline</v-icon>
                </button-tooltip>
                <button-tooltip text tooltip="Borrar invitación" @click="showDelete(invitation)">
                    <v-icon size="18" color="error">mdi-trash-can-outline</v-icon>
                </button-tooltip>
            </div>

            <div class="inv-footer">
                <span class="inv-count">{{ filtered.length }} pendientes</span>
                <kimsa-pagination :value="page" :page-count="pageCount" @page="page = $event" center />
                <v-btn v-if="canLoadMore" text small color="secondary" class="inv-more" :loading="loading"
                    @click="loadMore">
                    Cargar más
                </v-btn>
            </div>
        </div>

        <div v-else class="inv-empty">
            <v-icon size="34" color="txtMuted">mdi-email-off-outline</v-icon>
            <span>No hay invitaciones pendientes.</span>
        </div>

        <kimsa-confirm ref="confirm" alert-mode show-cancel title="Borrar invitación" :content="confirmContent"
            :persistent="false" :close-on-confirm="false" :loading="deleting" @confirm="deleteInvitation" />

        <kimsa-confirm ref="confirmBulk" alert-mode show-cancel title="Borrar invitaciones"
            :content="bulkContent" :persistent="false" :close-on-confirm="false" :loading="deleting"
            @confirm="deleteSelected" />

        <kimsa-confirm ref="confirmResend" show-cancel :title="resendTooltip" :content="resendContent"
            :persistent="false" :close-on-confirm="false" :loading="!!resendingId" @confirm="resendInvitation" />
    </admin-page>
</template>

<script>
import KimsaTextField from '@/components/common/inputs/KimsaTextField.vue'
import KimsaConfirm from '@/components/common/KimsaConfirm.vue'
import SegmentedTabs from '@/components/common/SegmentedTabs.vue'
import SkeletonLoader from '@/components/common/SkeletonLoader.vue'
import ButtonTooltip from '@/components/common/ButtonTooltip.vue'
import KimsaPagination from '@/components/common/KimsaPagination.vue'
import AdminPage from '@/components/admin/AdminPage.vue'
import firebase from 'firebase/app'
import 'firebase/functions'
import { db } from '@/utils/Firebase'
import { adminRouteGuard } from '@/utils/Admin'

export default {
    name: 'AdminInvitations',
    components: { KimsaTextField, KimsaConfirm, SegmentedTabs, SkeletonLoader, ButtonTooltip, KimsaPagination, AdminPage },
    beforeRouteEnter: adminRouteGuard,
    data() {
        return {
            type: 'student',
            typeOptions: [
                { name: 'A alumnos', value: 'student' },
                { name: 'A entrenadores', value: 'coach' },
            ],
            invitations: [],
            search: '',
            page: 1,
            perPage: 10,
            limit: 100,
            reachedEnd: false,
            loading: false,
            deleting: false,
            pending: null,
            selected: [],
            resendingId: null,
            result: null,
        }
    },
    computed: {
        isStudentType(vm = this) {
            return vm.type === 'student'
        },
        resendTooltip(vm = this) {
            return vm.isStudentType ? 'Reenviar invitación' : 'Reenviar con clave nueva'
        },
        resendContent(vm = this) {
            if (vm.isStudentType)
                return `Se le vuelve a enviar el mail de invitación a <b>${vm.pending?.email || ''}</b>.`
            return `Se le vuelve a enviar el mail a <b>${vm.pending?.email || ''}</b> con una <b>clave nueva</b>: la anterior deja de servir.`
        },
        allSelected(vm = this) {
            return vm.paged.length > 0 && vm.paged.every(invitation => vm.selected.includes(invitation.id))
        },
        bulkContent(vm = this) {
            return `Vas a borrar <b>${vm.selected.length}</b> invitación(es) pendiente(s).`
        },
        filtered(vm = this) {
            const search = vm.search.trim().toLowerCase()
            if (!search) return vm.invitations
            return vm.invitations.filter(invitation => (invitation.email || '').toLowerCase().includes(search))
        },
        pageCount(vm = this) {
            return Math.ceil(vm.filtered.length / vm.perPage) || 1
        },
        paged(vm = this) {
            const start = (vm.page - 1) * vm.perPage
            return vm.filtered.slice(start, start + vm.perPage)
        },
        canLoadMore(vm = this) {
            return !vm.reachedEnd && vm.invitations.length >= vm.limit
        },
        confirmContent(vm = this) {
            return `Vas a borrar la invitación pendiente de <b>${vm.pending?.email || ''}</b>.`
        },
    },
    mounted() {
        this.loadInvitations()
    },
    methods: {
        onChangeType(type) {
            const vm = this
            vm.type = type
            vm.limit = 100
            vm.reachedEnd = false
            vm.loadInvitations()
        },
        isSelected(invitation) {
            return this.selected.includes(invitation.id)
        },
        toggleSelect(invitation) {
            const vm = this
            vm.selected = vm.isSelected(invitation)
                ? vm.selected.filter(id => id !== invitation.id)
                : [...vm.selected, invitation.id]
        },
        toggleAll() {
            const vm = this
            const ids = vm.paged.map(invitation => invitation.id)
            vm.selected = vm.allSelected
                ? vm.selected.filter(id => !ids.includes(id))
                : [...new Set([...vm.selected, ...ids])]
        },
        showResend(invitation) {
            this.pending = invitation
            this.$refs.confirmResend.show()
        },
        async resendInvitation() {
            const vm = this
            const invitation = vm.pending
            if (!invitation) return
            vm.resendingId = invitation.id
            vm.result = null
            try {
                const fn = firebase.functions().httpsCallable('httpAdmin-resendInvitation')
                const { data } = await fn({ id: invitation.id, collection: invitation.collection })
                vm.result = {
                    type: 'success',
                    message: `Invitación reenviada a ${invitation.email}${data.newPassword ? ' con una clave nueva' : ''}.`
                }
            } catch (err) {
                console.error('[adminInvitations] resend', err)
                vm.result = { type: 'error', message: err.message || 'Error al reenviar la invitación.' }
            } finally {
                vm.resendingId = null
                vm.pending = null
                vm.$refs.confirmResend?.hide()
            }
        },
        async deleteSelected() {
            const vm = this
            vm.deleting = true
            vm.result = null
            try {
                const ids = [...vm.selected]
                for (let i = 0; i < ids.length; i += 400) {
                    const batch = db.batch()
                    for (const id of ids.slice(i, i + 400)) {
                        const invitation = vm.invitations.find(item => item.id === id)
                        if (invitation) batch.delete(db.collection(invitation.collection).doc(id))
                    }
                    await batch.commit()
                }
                vm.invitations = vm.invitations.filter(invitation => !ids.includes(invitation.id))
                vm.selected = []
                vm.result = { type: 'success', message: `${ids.length} invitación(es) borrada(s).` }
            } catch (err) {
                console.error('[adminInvitations] bulk delete', err)
                vm.result = { type: 'error', message: err.message || 'Error al borrar las invitaciones.' }
            } finally {
                vm.deleting = false
                vm.$refs.confirmBulk?.hide()
            }
        },
        loadMore() {
            this.limit += 200
            this.loadInvitations()
        },
        async loadInvitations() {
            const vm = this
            vm.result = null
            vm.selected = []
            await vm.withLoading(async () => {
                const isStudent = vm.type === 'student'
                const collection = isStudent ? 'studentInvitations' : 'coachInvitations'
                const snapshot = await db.collection(collection)
                    .where('status', '==', 'pending')
                    .limit(vm.limit)
                    .get()

                vm.reachedEnd = snapshot.size < vm.limit
                vm.page = 1

                vm.invitations = snapshot.docs.map(doc => {
                    const data = doc.data() || {}
                    const date = data.createdAt || data.created_at || null
                    const parsed = date ? vm.$moment(date.toDate ? date.toDate() : date) : null
                    return {
                        id: doc.id,
                        collection,
                        email: isStudent ? data.studentEmail : data.email,
                        status: data.status,
                        timestamp: parsed && parsed.isValid() ? parsed.valueOf() : 0,
                        fromLabel: isStudent
                            ? `De ${data.coach?.name || 'entrenador sin nombre'}`
                            : `De ${data.company?.name || 'empresa sin nombre'}`,
                        dateLabel: parsed && parsed.isValid() ? parsed.format('DD/MM/YYYY') : 'sin fecha',
                    }
                }).sort((a, b) => b.timestamp - a.timestamp)
            })
        },
        showDelete(invitation) {
            this.pending = invitation
            this.$refs.confirm.show()
        },
        async deleteInvitation() {
            const vm = this
            vm.deleting = true
            try {
                await db.collection(vm.pending.collection).doc(vm.pending.id).delete()
                vm.invitations = vm.invitations.filter(invitation => invitation.id !== vm.pending.id)
                vm.selected = vm.selected.filter(id => id !== vm.pending.id)
                vm.result = { type: 'success', message: `Invitación de ${vm.pending.email} borrada.` }
            } catch (err) {
                console.error('[adminInvitations] delete', err)
                vm.result = { type: 'error', message: err.message || 'Error al borrar la invitación.' }
            } finally {
                vm.deleting = false
                vm.pending = null
                vm.$refs.confirm?.hide()
            }
        },
    },
}
</script>

<style scoped>
.inv-toolbar {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
}

.inv-tabs {
    width: 260px;
}

.inv-search {
    flex: 1;
    min-width: 200px;
}

.inv-refresh {
    color: var(--v-txtMuted-base);
}

.inv-list {
    margin-top: 16px;
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.inv-select-bar {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 4px;
}

.inv-selected-count {
    flex: 1;
    font-size: 11px;
    color: var(--v-txtMuted-base);
}

.inv-bulk {
    text-transform: none;
    letter-spacing: normal;
}

.inv-check {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    flex-shrink: 0;
}

.inv-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    border-radius: 10px;
    border: 1px solid var(--v-outline-base);
    background: var(--v-surface2-base);
}

.inv-row--selected {
    border-color: var(--v-accent-base);
    background: rgba(51, 153, 153, 0.08);
}

.inv-body {
    flex: 1;
    min-width: 0;
}

.inv-email {
    font-size: 13px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.inv-meta {
    font-size: 11px;
    color: var(--v-txtMuted-base);
}

.inv-status {
    font-size: 11px;
    font-weight: 700;
    padding: 3px 10px;
    border-radius: 20px;
    background: rgba(230, 172, 0, 0.14);
    color: #E6AC00;
    white-space: nowrap;
}

.inv-footer {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    margin-top: 8px;
}

.inv-count {
    font-size: 11px;
    color: var(--v-txtMuted-base);
}

.inv-more {
    text-transform: none;
    letter-spacing: normal;
    margin-left: auto;
}

.inv-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    padding: 32px 12px;
    color: var(--v-txtMuted-base);
    font-size: 14px;
}

@media (max-width: 600px) {
    .inv-tabs {
        width: 100%;
    }
}
</style>
